import type { DrawingKind, MagnetMode } from "lightweight-charts-drawing"
import { useSyncExternalStore } from "react"

// The drawing toolbar's state, shared by every chart pane: the armed tool
// applies to the active pane; the other settings apply to all panes.

export interface DrawingToolbarState {
  /** The armed tool, or null for the cursor. */
  readonly tool: DrawingKind | null
  readonly magnet: MagnetMode
  /** TradingView "Keep drawing": the tool stays armed after a placement. */
  readonly stayInDrawingMode: boolean
  /** TradingView "Hide all drawings" (not saved). */
  readonly drawingsVisible: boolean
  /** Bumped by "Remove all drawings"; panes clear their symbol's drawings when it changes. */
  readonly clearRequest: number
}

let state: DrawingToolbarState = {
  tool: null,
  magnet: "off",
  stayInDrawingMode: false,
  drawingsVisible: true,
  clearRequest: 0
}
const listeners = new Set<() => void>()

const update = (patch: Partial<DrawingToolbarState>) => {
  state = { ...state, ...patch }
  for (const listener of listeners) listener()
}

export const drawingToolbar = {
  get: (): DrawingToolbarState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  setTool: (tool: DrawingKind | null) => update({ tool, drawingsVisible: tool === null ? state.drawingsVisible : true }),
  setMagnet: (magnet: MagnetMode) => update({ magnet }),
  setStayInDrawingMode: (stayInDrawingMode: boolean) => update({ stayInDrawingMode }),
  setDrawingsVisible: (drawingsVisible: boolean) => update({ drawingsVisible }),
  requestClear: () => update({ clearRequest: state.clearRequest + 1 })
}

export const useDrawingToolbar = (): DrawingToolbarState => useSyncExternalStore(drawingToolbar.subscribe, drawingToolbar.get)
